import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, Sparkles, Leaf, Users, TrendingUp, Heart, Zap, Star, Gift, Crown, Shield, Trophy, User, CheckCircle } from 'lucide-react';
import { authApi } from './AuthService';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/ApiUrl';
import { Link } from 'react-router-dom';
import AvatarOnboardingDialog from '../profile_component/AvatarSelectorDialog';
import { useTheme } from '../arrangement/ThemeContext';
const SmokeParticle = ({ delay = 0 }) => {
  const particleRef = useRef(null);
  
  useEffect(() => {
    const particle = particleRef.current;
    if (!particle) return;
    
    const animateParticle = () => {
      particle.style.animation = 'none';
      setTimeout(() => {
        particle.style.animation = `smoke ${3 + Math.random() * 2}s ease-out infinite`;
      }, delay);
    };
    
    animateParticle();
  }, [delay]);
  
  return (
    <div
      ref={particleRef}
      style={{
        position: 'absolute',
        width: `${4 + Math.random() * 8}px`,
        height: `${4 + Math.random() * 8}px`,
        background: `rgba(255, 255, 255, ${0.1 + Math.random() * 0.3})`,
        borderRadius: '50%',
        left: `${Math.random() * 100}%`,
        bottom: '-10px',
        filter: 'blur(1px)',
        animation: `smoke ${3 + Math.random() * 2}s ease-out infinite`,
        animationDelay: `${delay}ms`
      }}
    />
  );
};

const FloatingIcon = ({ icon: Icon, delay = 0, color = '#00d4aa' }) => {
  return (
    <div
      style={{
        position: 'absolute',
        animation: `float ${4 + Math.random() * 2}s ease-in-out infinite`,
        animationDelay: `${delay}ms`,
        left: `${Math.random() * 80 + 10}%`,
        top: `${Math.random() * 80 + 10}%`,
        opacity: 0.1,
        pointerEvents: 'none'
      }}
    >
      <Icon size={24 + Math.random() * 16} color={color} />
    </div>
  );
};

const GamificationBadge = ({ icon, text, earned = false, pulse = false }) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: earned 
          ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' 
          : 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        border: `1px solid ${earned ? '#fbbf24' : 'rgba(255, 255, 255, 0.2)'}`,
        fontSize: 'clamp(10px, 2vw, 12px)', // Responsive font size
        fontWeight: '500',
        color: earned ? '#92400e' : '#e5e5e5',
        animation: pulse ? 'pulse 2s infinite' : 'none',
        transition: 'all 0.3s ease'
      }}
    >
      <span style={{ fontSize: 'clamp(12px, 2.5vw, 14px)' }}>{icon}</span>
      {text}
    </div>
  );
};

const SmokerLevelCard = ({ level, emoji, description, isSelected, onClick }) => {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 'clamp(12px, 3vw, 16px)', // Responsive padding
        background: isSelected ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' : 'rgba(255, 255, 255, 0.1)',
        border: `2px solid ${isSelected ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        textAlign: 'center',
        transform: isSelected ? 'scale(1.05)' : 'scale(1)'
      }}
    >
      <div style={{ fontSize: 'clamp(18px, 4vw, 24px)', marginBottom: '8px' }}>{emoji}</div>
      <div style={{ 
        fontSize: 'clamp(12px, 2.5vw, 14px)', 
        fontWeight: '600', 
        color: isSelected ? 'white' : '#e5e5e5', 
        marginBottom: '4px',
        textTransform: 'capitalize'
      }}>
        {level}
      </div>
      <div style={{ 
        fontSize: 'clamp(9px, 2vw, 11px)', 
        color: isSelected ? 'rgba(255, 255, 255, 0.9)' : '#888', 
        lineHeight: '1.3' 
      }}>
        {description}
      </div>
    </div>
  );
};

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [loginStreak, setLoginStreak] = useState(7);
  const [particles, setParticles] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  const { colors } = useTheme();

  const { login } = useUser();

  const [showAvatarOnboarding, setShowAvatarOnboarding] = useState(false);


  const [smokerLevel, setSmokerLevel] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate smoke particles
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = Array.from({ length: isMobile ? 8 : 15 }, (_, i) => ({
        id: i,
        delay: i * 200 + Math.random() * 1000
      }));
      setParticles(newParticles);
    };

    generateParticles();
    const interval = setInterval(generateParticles, 5000);
    return () => clearInterval(interval);
  }, [isMobile]);

  useEffect(() => {
    setError('');
    setSuccess('');
    setEmail('');
    setPassword('');
    setUsername('');
    setSmokerLevel('');
  }, [isLogin]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await authApi.getCurrentUser();
        setTimeout(() => {
          navigate('/community');
        }, 1500);
      } catch (error) {
        // User not logged in, stay on login page
      }
    };
    
    checkAuth();
  }, []);

  useEffect(() => {
    const fetchCommunityStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/community-stats-login`);
        const result = await response.json();
        
        if (result.success) {
          const stats = result.stats;
          
          setCommunityStats([
            { 
              icon: '👥', 
              value: formatStatValue(stats.total_members), 
              label: 'Members' 
            },
            { 
              icon: '💬', 
              value: formatStatValue(stats.active_today), 
              label: 'Active Users' 
            },
            { 
              icon: '🌿', 
              value: formatStatValue(stats.total_reviews), 
              label: 'Strain Reviews' 
            },
            { 
              icon: '📝', 
              value: formatStatValue(stats.total_threads), 
              label: 'Threads' 
            }
          ]);
        }
      } catch (error) {
        console.error('Failed to fetch community stats:', error);
        // Keep default values on error
      }
    };

    fetchCommunityStats();
  }, []);

  const formatStatValue = (value) => {
    if (value >= 10000) {
      return `${(value / 1000).toFixed(1)}K`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    } else {
      return value.toString();
    }
  };

  const validateForm = () => {
    if (!email || !password) {
      setError('Email and password are required');
      return false;
    }
    if (!isLogin) {
      if (!username) {
        setError('Username is required');
        return false;
      }
      if (!smokerLevel) {
        setError('Please select your smoker level');
        return false;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) return;
    setIsLoading(true);

    try {
      let response;
      if (isLogin) {
        response = await authApi.login({ email, password });
      } else {
        response = await authApi.register({ username, email, password, smoker_level: smokerLevel });
      }

      console.log('Cookies after login:', document.cookie);
      
      if (response.success) {
        setSuccess(isLogin ? 'Welcome back!' : 'Account created successfully!');
        login(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        if (isLogin) {
          // For login, navigate directly
          setTimeout(() => {
            navigate('/community');
          }, 1500);
        } else {
          // For registration, show avatar onboarding
          setTimeout(() => {
            setShowAvatarOnboarding(true);
          }, 1000);
        }
      } else {
        setError(response.error || 'Something went wrong');
      }
    } catch (err) {
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const smokerLevels = [
    { level: 'beginner', emoji: '🌱', description: 'New to cannabis culture' },
    { level: 'casual', emoji: '🌿', description: 'Occasional enjoyment' },
    { level: 'connoisseur', emoji: '🍃', description: 'Knowledgeable enthusiast' },
    { level: 'stoner', emoji: '🔥', description: 'Daily lifestyle choice' }
  ];

  const socialLogins = [
    { name: 'Google', icon: '🌐', color: '#4285f4' },
    { name: 'Discord', icon: '🎮', color: '#5865f2' },
    { name: 'Apple', icon: '🍎', color: '#000000' }
  ];

  const [communityStats, setCommunityStats] = useState([
    { icon: '👥', value: '...', label: 'Members' },
    { icon: '💬', value: '...', label: 'Active Today' },
    { icon: '🌿', value: '...', label: 'Strain Reviews' },
    { icon: '📝', value: '...', label: 'Threads' }
  ]);
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.bgPrimary} 0%, ${colors.bgSecondary} 50%, ${colors.bgPrimary} 100%)`,
      color: colors.textPrimary,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes smoke {
          0% {
            opacity: 0.3;
            transform: translateY(0) scale(1) rotate(0deg);
          }
          50% {
            opacity: 0.6;
            transform: translateY(-100px) scale(1.5) rotate(180deg);
          }
          100% {
            opacity: 0;
            transform: translateY(-200px) scale(2) rotate(360deg);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(0, 212, 170, 0.3);
          }
          50% {
            box-shadow: 0 0 40px rgba(0, 212, 170, 0.6);
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .slide-in {
          animation: slideIn 0.6s ease-out;
        }
        
        .glow-effect {
          animation: glow 3s ease-in-out infinite;
        }

        /* Mobile responsive breakpoints */
        @media (max-width: 768px) {
          .desktop-grid {
            display: flex !important;
            flex-direction: column !important;
            gap: 32px !important;
            padding: 20px !important;
          }
          
          .form-container {
            max-width: none !important;
            width: 100% !important;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          
          .social-grid {
            grid-template-columns: repeat(1, 1fr) !important;
            gap: 8px !important;
          }
          
          .smoker-level-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
        }
        
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: repeat(1, 1fr) !important;
          }
          
          .floating-cta {
            display: none !important;
          }
        }
      `}</style>

      {/* Floating Background Icons - Fewer on mobile */}
      {[Leaf, Users, Heart, Star, Crown, Shield, Sparkles, Zap]
        .slice(0, isMobile ? 4 : 8)
        .map((Icon, index) => (
        <FloatingIcon 
          key={index} 
          icon={Icon} 
          delay={index * 500}
          color={['#00d4aa', '#10b981', '#8b5cf6', '#fbbf24'][index % 4]}
        />
      ))}

      {/* Smoke Effect Container */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: isMobile ? '150px' : '300px', // Shorter on mobile
        pointerEvents: 'none',
        overflow: 'hidden'
      }}>
        {particles.map(particle => (
          <SmokeParticle key={particle.id} delay={particle.delay} />
        ))}
      </div>

      <div className="desktop-grid" style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 400px',
        minHeight: '100vh',
        maxWidth: '1200px',
        margin: '0 auto',
        gap: isMobile ? '32px' : '60px',
        padding: isMobile ? '20px' : '40px',
        alignItems: 'center'
      }}>
        {/* Left Side - Branding & Community Stats */}
        <div className="slide-in" style={{ 
          animationDelay: '0.2s',
          order: isMobile ? 2 : 1 // Move to bottom on mobile
        }}>
          {/* Logo and Main Heading */}
          <div style={{ marginBottom: isMobile ? '32px' : '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '24px',
              justifyContent: isMobile ? 'center' : 'flex-start' // Center on mobile
            }}>
              <div style={{
                width: isMobile ? '60px' : '80px', // Smaller logo on mobile
                height: isMobile ? '60px' : '80px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                borderRadius: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 30px rgba(16, 185, 129, 0.4)'
              }} className="glow-effect">
                <img 
                  src={`${process.env.PUBLIC_URL}/blackicon.svg`}
                  alt="HighMind Logo"
                  style={{
                    width: isMobile ? '40px' : '54px',
                    height: isMobile ? '40px' : '54px',
                  }}
                />
              </div>
              <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                <h1 style={{
                  margin: 0,
                  fontSize: isMobile ? '36px' : '48px', // Smaller on mobile
                  fontWeight: '900',
                  background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 50%, #8b5cf6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: '1.1'
                }}>
                  Cannabis
                </h1>
                <h2 style={{
                  margin: 0,
                  fontSize: isMobile ? '24px' : '32px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  lineHeight: '1.1'
                }}>
                  Community
                </h2>
              </div>
            </div>
            
            <p style={{
              fontSize: isMobile ? '16px' : '20px',
              color: '#888',
              marginBottom: '32px',
              lineHeight: '1.6',
              maxWidth: '500px',
              textAlign: isMobile ? 'center' : 'left',
              margin: isMobile ? '0 auto 32px auto' : '0 0 32px 0'
            }}>
              Join the most vibrant cannabis community where knowledge meets culture. 
              Connect with fellow enthusiasts, share experiences, and grow together.
            </p>

            {/* Gamification Preview - Simplified on mobile */}
            {!isMobile && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                padding: '24px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                marginBottom: '32px'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Trophy size={20} style={{ color: '#fbbf24' }} />
                  Your Journey Awaits
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '12px'
                }}>
                  <GamificationBadge icon="🌱" text="First Post" earned />
                  <GamificationBadge icon="🔥" text="Trending" pulse />
                  <GamificationBadge icon="💎" text="Premium Member" />
                  <GamificationBadge icon="👑" text="Community Leader" />
                </div>
              </div>
            )}
          </div>

          {/* Community Stats */}
          <div className="stats-grid" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
            gap: isMobile ? '12px' : '20px'
          }}>
            {communityStats.map((stat, index) => (
              <div
                key={index}
                className="slide-in"
                style={{
                  animationDelay: `${0.4 + index * 0.1}s`,
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '16px',
                  padding: isMobile ? '16px' : '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 212, 170, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(0, 212, 170, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ 
                  fontSize: isMobile ? '20px' : '24px', 
                  marginBottom: '8px' 
                }}>{stat.icon}</div>
                <div style={{
                  fontSize: isMobile ? '20px' : '28px',
                  fontWeight: '700',
                  color: '#00d4aa',
                  marginBottom: '4px'
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: isMobile ? '10px' : '12px',
                  color: '#888',
                  fontWeight: '500'
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="slide-in form-container" style={{ 
          animationDelay: '0.4s',
          order: isMobile ? 1 : 2, // Move to top on mobile
          maxWidth: isMobile ? 'none' : '400px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '24px',
            padding: isMobile ? '24px' : '40px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            {/* Form Header with Streak */}
            <div style={{ 
              textAlign: 'center', 
              marginBottom: isMobile ? '24px' : '32px' 
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                padding: '8px 16px',
                borderRadius: '20px',
                marginBottom: '16px',
                border: '1px solid #fbbf24'
              }}>
                <Zap size={16} style={{ color: '#f59e0b' }} />
                <span style={{
                  color: '#92400e',
                  fontSize: isMobile ? '11px' : '12px',
                  fontWeight: '600'
                }}>
                  Start a Streak!
                </span>
              </div>
              
              <h2 style={{
                margin: '0 0 8px 0',
                fontSize: isMobile ? '24px' : '32px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #e5e5e5 0%, #ffffff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {isLogin ? 'Welcome Back!' : 'Join the Community'}
              </h2>
              <p style={{
                margin: 0,
                color: '#888',
                fontSize: isMobile ? '14px' : '16px'
              }}>
                {isLogin 
                  ? 'Continue your cannabis journey' 
                  : 'Start your cannabis community experience'
                }
              </p>
            </div>

            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '24px',
                color: '#fca5a5',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '12px',
                padding: '12px 16px',
                marginBottom: '24px',
                color: '#86efac',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle size={16} />
                {success}
              </div>
            )}

            {/* Social Login Buttons */}
            {/* <div style={{ marginBottom: '32px' }}>
              <div className="social-grid" style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)',
                gap: isMobile ? '8px' : '12px'
              }}>
                {socialLogins.map((social, index) => (
                  <button
                    key={social.name}
                    style={{
                      padding: isMobile ? '12px 16px' : '16px 12px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '16px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: isMobile ? 'row' : 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: isMobile ? '12px' : '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.borderColor = social.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                  >
                    <span style={{ fontSize: isMobile ? '18px' : '20px' }}>{social.icon}</span>
                    <span style={{ fontSize: isMobile ? '14px' : '12px' }}>
                      Continue with {social.name}
                    </span>
                  </button>
                ))}
              </div>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                margin: '24px 0',
                opacity: 0.6
              }}>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.2)' }} />
                <span style={{ fontSize: '14px', color: '#888' }}>or continue with email</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.2)' }} />
              </div>
            </div> */}

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              {!isLogin && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#e5e5e5'
                  }}>
                    Username
                  </label>
                  <div style={{ position: 'relative' }}>
                    <User size={20} style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: focusedField === 'username' ? '#00d4aa' : '#888',
                      transition: 'color 0.3s ease'
                    }} />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      onFocus={() => setFocusedField('username')}
                      onBlur={() => setFocusedField('')}
                      placeholder="Choose your username"
                      style={{
                        width: '100%',
                        paddingLeft: '52px',
                        paddingRight: '20px',
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        border: `2px solid ${focusedField === 'username' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                        borderRadius: '16px',
                        color: 'white',
                        fontSize: '16px',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5'
                }}>
                  {isLogin ? 'Email or Username' : 'Email'}
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={20} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: focusedField === 'email' ? '#00d4aa' : '#888',
                    transition: 'color 0.3s ease'
                  }} />
                  <input
                    type={isLogin ? 'text' : 'email'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField('')}
                    placeholder={isLogin ? 'your@email.com or username' : 'your@email.com'}
                    style={{
                      width: '100%',
                      paddingLeft: '52px',
                      paddingRight: '20px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: `2px solid ${focusedField === 'email' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                      borderRadius: '16px',
                      color: 'white',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#e5e5e5'
                }}>
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={20} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: focusedField === 'password' ? '#00d4aa' : '#888',
                    transition: 'color 0.3s ease'
                  }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField('')}
                    placeholder="Enter your password"
                    style={{
                      width: '100%',
                      paddingLeft: '52px',
                      paddingRight: '52px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: `2px solid ${focusedField === 'password' ? '#00d4aa' : 'rgba(255, 255, 255, 0.2)'}`,
                      borderRadius: '16px',
                      color: 'white',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: '#888',
                      cursor: 'pointer',
                      padding: '4px',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#00d4aa'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {!isLogin && (
                <div style={{ marginBottom: '32px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#e5e5e5'
                  }}>
                    Choose Your Smoker Level
                  </label>
                  <div className="smoker-level-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(2, 1fr)',
                    gap: isMobile ? '8px' : '12px'
                  }}>
                    {smokerLevels.map((level) => (
                      <SmokerLevelCard
                        key={level.level}
                        level={level.level}
                        emoji={level.emoji}
                        description={level.description}
                        isSelected={smokerLevel === level.level}
                        onClick={() => setSmokerLevel(level.level)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Action Button */}
              {isLogin && (
                <div style={{ 
                  textAlign: 'right', 
                  marginBottom: '24px' 
                }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      color: '#888',
                      fontSize: '14px',
                      textDecoration: 'none',
                      transition: 'color 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#00d4aa'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#888'}
                  >
                    Forgot your password?
                  </Link>
                </div>
              )}
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: isMobile ? '16px' : '18px',
                  background: isLoading 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px'
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 30px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {isLoading ? (
                  <>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    {isLogin ? 'Signing In...' : 'Creating Account...'}
                  </>
                ) : (
                  <>
                    <Sparkles size={20} />
                    {isMobile 
                      ? (isLogin ? 'Sign In' : 'Join Community')
                      : (isLogin ? 'Sign In & Continue Journey' : 'Join the Community')
                    }
                  </>
                )}
              </button>

              {/* Toggle Login/Signup */}
              <div style={{ textAlign: 'center' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>
                  {isLogin ? "Don't have an account? " : "Already have an account? "}
                </span>
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#00d4aa',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#10b981'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#00d4aa'}
                >
                  {isLogin ? 'Sign up for free' : 'Sign in instead'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Floating Call-to-Action - Hidden on small screens */}
      <div className="floating-cta" style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        borderRadius: '50px',
        padding: isMobile ? '12px 16px' : '16px 24px',
        display: isMobile ? 'none' : 'flex', // Hide on mobile
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 12px 30px rgba(139, 92, 246, 0.5)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1) translateY(0)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.4)';
      }}
      >
        <Gift size={20} style={{ color: 'white' }} />
        <span style={{
          color: 'white',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          Get 100 XP for joining today!
        </span>
      </div>
      <AvatarOnboardingDialog
        isOpen={showAvatarOnboarding}
        onClose={() => {
          setShowAvatarOnboarding(false);
          navigate('/community');
        }}
        onAvatarSelect={(avatarUrl) => {
          // Avatar was selected and updated
          setShowAvatarOnboarding(false);
          navigate('/community');
        }}
        onSkip={() => {
          
          // User skipped avatar selection
          setShowAvatarOnboarding(false);
          navigate('/community');
        }}
        API_URL={API_URL}
        userName={username}
        isOnboarding={true}
      />
    </div>
  );
}